import React from 'react';
import {
  LuCheck, LuLayers, LuFileText, LuImages, LuClipboardList, LuLoader,
  LuRotateCcw, LuArrowLeft, LuTriangleAlert
} from 'react-icons/lu';
import { useCrawlerDiscoveryOverview } from '../../../hooks/API/wizard.api';
import DiscoveryScreenshotGallery from '../../wizard/steps/discovery/DiscoveryScreenshotGallery';

const formatCount = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return '0';
  return num.toLocaleString('en-US');
};

/**
 * Crawl results for the in-app "Crawl Project" page: discovery overview,
 * element breakdown and crawled screenshots (the summary half of the wizard's
 * Step10CReviewDiscovery, without the test case list).
 */
const CrawlResultsPanel = ({ projectId, onNewCrawl, onBack }) => {
  // Always refetch: the cached overview may predate the crawl that just finished
  const {
    data: overview,
    isLoading,
    isError
  } = useCrawlerDiscoveryOverview(projectId, { refetchOnMount: 'always' });

  const summary = {
    pages: overview?.total_pages ?? 0,
    screenshots: overview?.total_screenshots ?? 0,
    elements: overview?.total_elements ?? 0,
    forms: overview?.total_forms_mapped ?? 0,
    formElements: overview?.total_form_elements ?? 0,
    buttons: overview?.total_buttons ?? 0,
    links: overview?.total_links ?? 0,
    inputs: overview?.total_inputs ?? 0
  };

  const summaryCards = [
    { label: 'Pages', count: summary.pages, icon: LuFileText, bg: 'bg-[var(--primary-muted)]', text: 'text-blue-600' },
    { label: 'Screenshots', count: summary.screenshots, icon: LuImages, bg: 'bg-[var(--warning-muted)]', text: 'text-amber-600' },
    { label: 'Elements', count: summary.elements, icon: LuLayers, bg: 'bg-[var(--success-muted)]', text: 'text-emerald-600' },
    { label: 'Forms', count: summary.forms, icon: LuClipboardList, bg: 'bg-[rgba(139,92,246,0.12)]', text: 'text-violet-600' }
  ];

  const elementBreakdown = [
    { label: 'Form Elements', count: summary.formElements, bar: 'bg-amber-500', text: 'text-amber-600' },
    { label: 'Buttons', count: summary.buttons, bar: 'bg-blue-500', text: 'text-blue-600' },
    { label: 'Links', count: summary.links, bar: 'bg-sky-500', text: 'text-sky-600' },
    { label: 'Input Fields', count: summary.inputs, bar: 'bg-emerald-500', text: 'text-emerald-600' }
  ];

  const breakdownDenominator = summary.elements > 0 ? summary.elements : 1;

  return (
    <div className="space-y-5">
      {/* Banner */}
      <div
        className={`flex flex-col gap-4 rounded-2xl border p-5 shadow-sm sm:flex-row sm:items-center sm:p-6 ${
          isError ? 'border-[color-mix(in_srgb,var(--warning)_35%,transparent)] bg-[var(--warning-muted)]' : 'border-[color-mix(in_srgb,var(--success)_35%,transparent)] bg-[var(--success-muted)]'
        }`}
      >
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white shadow-md ${
            isError ? 'bg-amber-500' : 'bg-emerald-500'
          }`}
        >
          {isError ? <LuTriangleAlert size={24} /> : <LuCheck size={24} />}
        </div>
        <div className="min-w-0 flex-1">
          <h2 className={`text-lg font-semibold ${isError ? 'text-[color:var(--warning)]' : 'text-[color:var(--success)]'}`}>
            {isError ? 'UI Map Created' : 'UI Map Ready'}
          </h2>
          <p className="mt-0.5 text-sm text-[color:var(--text-secondary)]">
            {isLoading
              ? 'Loading discovery overview...'
              : isError
                ? 'Discovery overview could not be loaded'
                : `Discovered ${formatCount(summary.pages)} pages with ${formatCount(summary.elements)} UI elements`}
          </p>
        </div>
        <button
          type="button"
          onClick={onNewCrawl}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--surface)] px-4 py-2.5 text-sm font-medium text-[color:var(--text-secondary)] shadow-sm transition hover:bg-[var(--bg-tertiary)] sm:w-auto"
        >
          <LuRotateCcw size={16} /> Run New Crawl
        </button>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {summaryCards.map(({ label, count, icon: Icon, bg, text }) => (
          <div key={label} className="flex items-center gap-3 rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4 shadow-sm">
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${bg}`}>
              <Icon size={22} className={text} />
            </div>
            <div className="min-w-0">
              {isLoading ? (
                <div className="mb-1 h-7 w-14 animate-pulse rounded bg-[var(--bg-tertiary)]" />
              ) : (
                <div className={`text-2xl font-bold leading-tight tabular-nums ${text}`}>{formatCount(count)}</div>
              )}
              <div className="truncate text-xs font-medium text-[color:var(--text-muted)]">{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Element breakdown */}
      <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-5 shadow-sm sm:p-6">
        <h3 className="flex items-center gap-2 text-base font-semibold text-[color:var(--text-primary)]">
          <LuLayers size={18} className="text-[color:var(--text-subtle)]" />
          Element Breakdown
        </h3>

        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-6 text-sm text-[color:var(--text-muted)]">
            <LuLoader size={18} className="animate-spin" />
            Loading element breakdown...
          </div>
        ) : (
          <div className="mt-4 space-y-3.5">
            {elementBreakdown.map((item) => {
              const width = Math.min((item.count / breakdownDenominator) * 100, 100);
              return (
                <div key={item.label} className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1.5 sm:grid-cols-[120px_1fr_80px]">
                  <div className="text-sm font-medium text-[color:var(--text-secondary)]">{item.label}</div>
                  <div className={`text-right text-sm font-semibold tabular-nums sm:order-last ${item.text}`}>
                    {formatCount(item.count)}
                  </div>
                  <div className="col-span-2 h-2.5 overflow-hidden rounded-full bg-[var(--bg-tertiary)] sm:col-span-1">
                    <div
                      className={`h-full rounded-full transition-[width] duration-500 ease-out ${item.bar}`}
                      style={{ width: `${width}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Crawled screenshots */}
      <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-4 shadow-sm sm:p-6">
        <DiscoveryScreenshotGallery projectId={projectId} totalScreenshots={overview?.total_screenshots} />
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[var(--primary-color)] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:brightness-110 sm:w-auto"
        >
          <LuArrowLeft size={16} /> Back to Projects
        </button>
      </div>
    </div>
  );
};

export default CrawlResultsPanel;
